import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ERRORS = {
  yetkisiz_hesap: "Bu Google hesabı uygulamaya erişim için yetkili değil.",
  oauth_basarisiz: "Google girişi tamamlanamadı.",
  kod_yok: "Google'dan yetkilendirme kodu alınamadı.",
  access_denied: "Google izin ekranında erişim reddedildi. Hesabın OAuth ekranındaki test kullanıcıları listesinde olduğundan emin olun.",
  redirect_uri_mismatch: "Google Console'daki Authorized redirect URI, uygulamanın adresiyle birebir aynı olmalı.",
  eksik_izin: "Drive ve Sheets izinlerinin ikisi de onaylanmalı.",
};

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { config, loginWithToken, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);

  useEffect(() => {
    const err = params.get("hata");
    const msg = params.get("mesaj");
    if (err) {
      toast.error(ERRORS[err] || "Giriş yapılamadı.", { duration: 9000 });
      setErrorDetail(`${ERRORS[err] || err}${msg ? ` — ${msg}` : ""}`);
    }
  }, [params]);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const googleLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/google/login");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google girişi başlatılamadı.");
      setBusy(false);
    }
  };

  const demoLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/demo-login");
      await loginWithToken(data.token);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error("Demo giriş yapılamadı.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-between bg-navy p-8 lg:p-12">
        <div className="flex h-[112px] w-[112px] items-center justify-center rounded-2xl bg-white p-3">
          <img src="/shb-logo.jpg" alt="SHB Sağlam Hukuk Bürosu" className="h-full w-full object-contain" />
        </div>
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-white">SHB Evrak Takip</h1>
          <p className="mt-2 text-base text-white/80">Sağlam Hukuk Bürosu</p>
          <p className="mt-3 text-sm text-white/60">Gelen Evrak · Yapılacak İş · Süre Takibi</p>
        </div>
        <p className="text-xs text-white/40">© 2026 Sağlam Hukuk Bürosu</p>
      </div>

      <div className="flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Giriş Yap</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Yalnızca yetkilendirilmiş Google hesabı giriş yapabilir.
          </p>
          {errorDetail && (
            <div
              className="mt-4 rounded-xl border border-crimson/30 bg-crimson/5 p-4 text-sm text-crimson"
              data-testid="login-error-detail"
            >
              {errorDetail}
            </div>
          )}
          <Button
            onClick={googleLogin}
            disabled={busy || !config?.google_configured}
            data-testid="google-login-btn"
            className="mt-6 w-full bg-navy text-white hover:bg-navy/90 dark:bg-slate-100 dark:text-navy"
          >
            Google ile Giriş Yap
          </Button>
          {!config?.google_configured && (
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-300" data-testid="google-not-configured">
              Google OAuth anahtarları henüz sunucuya eklenmedi. Anahtarlar eklenene kadar demo giriş ile
              uygulamayı inceleyebilirsiniz.
            </p>
          )}
          {config?.demo_login && (
            <Button variant="outline" onClick={demoLogin} disabled={busy} data-testid="demo-login-btn" className="mt-3 w-full">
              Demo Giriş (Drive'sız)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
